import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Shield, Flame, CloudRain, Compass,
  CheckCircle, Target, TreePine, Activity, Heart, Zap,
  ThumbsUp, Repeat, Eye
} from 'lucide-react';
import Header from '../components/Header';
import QMGSymbols from '../components/QMGSymbols';
import Footer from '../components/Footer';

const SKILLS = [
  {
    icon: CloudRain,
    title: 'Accepting Uncomfortable Emotions',
    description: 'Nerves before a big putt, frustration after a bad drive, anxiety on the first tee — these are normal human responses. Acceptance means allowing these feelings to exist without fighting them. When you stop battling your nerves, they lose their power over your performance.',
    onCourse: 'When you feel nerves rising, say to yourself: "I notice I\'m feeling anxious. That\'s okay — it means this matters to me." Then refocus on your target and process.',
    offCourse: 'Practise sitting with mild discomfort intentionally — cold exposure, holding a plank, or simply sitting still when you feel restless. Build your tolerance for discomfort.',
    inApp: 'Tag diary entries with "Accepting" when you successfully acknowledge difficult emotions without fighting them during a round.',
  },
  {
    icon: Eye,
    title: 'Cognitive Defusion',
    description: 'Your mind generates thousands of thoughts per round. Cognitive defusion is the skill of separating yourself from unhelpful thoughts — seeing them as mental noise, not truth. "I always choke on this hole" becomes "I\'m having the thought that I always choke on this hole."',
    onCourse: 'When a negative thought appears, prefix it with "I notice I\'m having the thought that..." This creates instant psychological distance. The thought is still there, but you\'re no longer fused with it.',
    offCourse: 'Write down your three most common negative golf thoughts. Read each one aloud, then read it again with "I\'m having the thought that..." in front. Notice how the emotional charge reduces.',
    inApp: 'Record recurring thoughts in your Diary. Over weeks, you\'ll see patterns and develop faster defusion skills.',
  },
  {
    icon: Shield,
    title: 'Reframing Challenge vs Threat',
    description: 'The same situation — a 150-yard approach over water — can be perceived as a threat ("Don\'t hit it in the water") or a challenge ("I\'m going to commit to my 7-iron and trust it"). Acceptance includes choosing to see pressure moments as opportunities rather than dangers, but more importantly you willingly accept all possible outcomes.',
    onCourse: 'Before any shot that triggers anxiety, reframe: "This is a chance to show what I can do" rather than "Don\'t mess this up." Focus on what you want to do, not what you want to avoid.',
    offCourse: 'Visualise your three most feared shots or situations on the course. Practise seeing each one as a challenge you welcome rather than a threat to avoid.',
    inApp: 'Track your "During the Round" Performance Scorecard scores — specifically emotional control and decision making. These directly reflect your threat-vs-challenge response.',
  },
  {
    icon: Repeat,
    title: 'Letting Go of Outcomes',
    description: 'Acceptance means releasing attachment to the result of each shot. You can control your preparation, your routine, and your commitment — but not where the ball ends up. Clinging to outcomes creates tension; releasing them creates freedom.',
    onCourse: 'After every shot, practise a "release ritual." Take one deep breath, let the result go (good or bad), and walk forward. The next shot deserves your full, fresh attention.',
    offCourse: 'Reflect on a recent round where you were outcome-focused. How did it affect your tension, decision-making, and enjoyment? Now imagine the same round with full process focus.',
    inApp: 'Your "In-Between Shots" scorecard score measures exactly this — your ability to let go and reset between shots.',
  },
  {
    icon: ThumbsUp,
    title: 'Self-Compassion Under Pressure',
    description: 'Most golfers are brutally self-critical after mistakes. Research shows self-compassion — treating yourself with the same kindness you\'d offer a friend — leads to faster recovery, better focus, and more consistent performance than self-criticism.',
    onCourse: 'After a bad shot, ask: "What would I say to my playing partner if they hit that?" Say the same thing to yourself. Replace "I\'m an idiot" with "Tough break, now let\'s focus on the next one."',
    offCourse: 'Keep a self-compassion journal. After each round, write down one mistake and respond to it as you would a friend. This rewires your default response pattern.',
    inApp: 'Use your Diary to practise compassionate self-reflection after rounds. Notice over time how your internal dialogue shifts.',
  },
];

const MACAcceptingPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-[#BC562B] py-10 md:py-14">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/mac-research" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-5 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to The Research
            </Link>
            <p className="text-white/50 text-sm uppercase tracking-[0.2em] mb-2">Step Two</p>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-3">
              Step Up — Accepting
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-2xl">
              Accept your thoughts and feelings without judgement. See your situation as a challenge, not a threat. This is the skill that truly separates golfers who struggle when internally challenged from those who are able to meet that internal challenge but still commit to their own personal values. It is this skill that more than not separates the winner from everyone else.
            </p>
            <div className="mt-6">
              <QMGSymbols active="acceptance" tone="light" size="md" />
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">

          {/* What It Means */}
          <section className="py-12 md:py-16" data-testid="what-it-means">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">What Acceptance Means in Golf</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-4">
                Acceptance is the most misunderstood part of the QMG Blueprint. It does <strong>not</strong> mean giving up, being passive, or accepting poor performance. It means <strong>accepting your internal experience</strong> — your thoughts, emotions, and physical sensations — without trying to suppress, control, or fight them.
              </p>
              <p className="text-[#57534E] leading-relaxed mb-4">
                Traditional sport psychology often teaches athletes to "think positively" or "control your emotions." But research consistently shows that <strong>trying to control or suppress unwanted thoughts paradoxically increases their frequency and intensity</strong>. Tell yourself "don't think about the water" and your mind fixates on the water.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                Acceptance offers a different path: allow the thought to exist, don't give it power, and redirect your attention to what you can control — your process, your routine, your commitment to the shot - YOUR PERSONAL VALUES.
              </p>
            </div>
          </section>

          {/* Acceptance vs Resistance Visual Deep-Dive */}
          <section className="pb-12 md:pb-16" data-testid="acceptance-vs-resistance-teaser">
            <Link
              to="/mac/acceptance-vs-resistance"
              className="block group bg-gradient-to-br from-[#1A2E1D] to-[#2C5530] text-white p-8 md:p-10 hover:shadow-xl transition-all"
              data-testid="link-acceptance-vs-resistance"
            >
              <div className="flex items-start md:items-center gap-6 flex-col md:flex-row">
                <div className="flex-shrink-0 flex gap-3">
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-lg border-2 border-white/20 shadow-lg bg-[#0E1A10] flex items-center justify-center overflow-hidden p-1">
                    <img
                      src="https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/9tf4vw7n_non-acceptance.png"
                      alt="Spiral of worry"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-lg border-2 border-white/20 shadow-lg bg-[#0E1A10] flex items-center justify-center overflow-hidden p-1">
                    <img
                      src="https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/gmvbwyjo_acceptance.png"
                      alt="Acceptance"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Visual Deep-Dive</p>
                  <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-2 group-hover:text-[#BC562B] transition-colors">
                    Acceptance vs. Resistance
                  </h3>
                  <p className="text-white/80 text-sm md:text-base leading-relaxed">
                    See the exact moment a poor shot becomes a mental spiral — and the exact moment it doesn't. Toggle between the two states of mind that decide your next shot.
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-white/60 group-hover:text-[#BC562B] group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
            </Link>
          </section>

          {/* Research Quote */}
          <section className="pb-12 md:pb-16" data-testid="research-quote">
            <div className="bg-[#F2F0E9] border-l-4 border-[#BC562B] p-6 md:p-8">
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "Efforts to control or suppress unwanted thoughts and emotions may paradoxically increase their frequency and intensity, impairing the very performance athletes seek to protect."
              </blockquote>
              <p className="text-[#737373] text-sm">— Inspired by Wegner's Ironic Process Theory, 1994 — Foundational principle of MAC</p>
            </div>
          </section>

          {/* The Experiential Avoidance Problem */}
          <section className="pb-12 md:pb-16" data-testid="avoidance-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">The Experiential Avoidance Problem</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-6">
                Research identifies <strong>experiential avoidance</strong> — the tendency to avoid or suppress uncomfortable internal experiences — as the primary psychological barrier to peak performance. The MAC studies consistently show that reducing experiential avoidance is the mechanism through which performance improves.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50 p-5">
                  <p className="text-red-800 font-semibold mb-3">What Avoidance Looks Like</p>
                  <ul className="text-sm text-red-700 space-y-2">
                    <li className="flex items-start gap-2"><Flame className="w-4 h-4 flex-shrink-0 mt-0.5" />Steering away from difficult pin positions</li>
                    <li className="flex items-start gap-2"><Flame className="w-4 h-4 flex-shrink-0 mt-0.5" />Rushing through your routine when nervous</li>
                    <li className="flex items-start gap-2"><Flame className="w-4 h-4 flex-shrink-0 mt-0.5" />Trying to "not think about" hazards</li>
                    <li className="flex items-start gap-2"><Flame className="w-4 h-4 flex-shrink-0 mt-0.5" />Getting angry to mask anxiety</li>
                    <li className="flex items-start gap-2"><Flame className="w-4 h-4 flex-shrink-0 mt-0.5" />Giving up internally after a bad stretch</li>
                  </ul>
                </div>
                <div className="bg-green-50 p-5">
                  <p className="text-green-800 font-semibold mb-3">What Acceptance Looks Like</p>
                  <ul className="text-sm text-green-700 space-y-2">
                    <li className="flex items-start gap-2"><Heart className="w-4 h-4 flex-shrink-0 mt-0.5" />Committing to the smart shot regardless of nerves</li>
                    <li className="flex items-start gap-2"><Heart className="w-4 h-4 flex-shrink-0 mt-0.5" />Completing your full routine even under pressure</li>
                    <li className="flex items-start gap-2"><Heart className="w-4 h-4 flex-shrink-0 mt-0.5" />Acknowledging the water and choosing your target</li>
                    <li className="flex items-start gap-2"><Heart className="w-4 h-4 flex-shrink-0 mt-0.5" />Feeling frustrated AND staying focused</li>
                    <li className="flex items-start gap-2"><Heart className="w-4 h-4 flex-shrink-0 mt-0.5" />Competing on every shot until the last putt drops</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Skills to Train */}
          <section className="pb-12 md:pb-16" data-testid="skills-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Skills to Train</h2>
            <p className="text-[#57534E] mb-8">Practical acceptance skills that transform your relationship with pressure.</p>

            <div className="space-y-8">
              {SKILLS.map((skill, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] overflow-hidden" data-testid={`skill-${i}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#BC562B] text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <skill.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">{skill.title}</h3>
                        <p className="text-[#57534E] leading-relaxed">{skill.description}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-[#2C5530]/5 p-4">
                        <p className="text-xs text-[#2C5530] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> On the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.onCourse}</p>
                      </div>
                      <div className="bg-[#BC562B]/5 p-4">
                        <p className="text-xs text-[#BC562B] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><TreePine className="w-3.5 h-3.5" /> Off the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.offCourse}</p>
                      </div>
                      <div className="bg-[#1A1A1A]/5 p-4">
                        <p className="text-xs text-[#1A1A1A] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Track in QMG</p>
                        <p className="text-sm text-[#57534E]">{skill.inApp}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* The Acceptance Paradox */}
          <section className="pb-12 md:pb-16" data-testid="paradox-section">
            <div className="bg-[#BC562B] p-6 md:p-10 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-4 text-white">The Acceptance Paradox</h2>
              <p className="text-white/90 leading-relaxed mb-4">
                Here's the paradox: when you stop trying to eliminate nervousness, it often reduces on its own. When you stop fighting frustration, it passes faster. When you accept that bad shots happen, you recover quicker.
              </p>
              <p className="text-white/90 leading-relaxed">
                Acceptance doesn't make you passive — it makes you <strong>free</strong>. Free to feel nervous and still commit. Free to feel frustrated and still focus. Free to play your game regardless of what's happening inside you.
              </p>
            </div>
          </section>

          {/* Navigation */}
          <section className="pb-8" data-testid="nav-section">
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/mac/mindful" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/50 hover:shadow-md transition-all group" data-testid="nav-mindful">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Previous Step</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#2C5530] transition-colors">Step Back — Awareness</h3>
                <p className="text-sm text-[#57534E]">Develop moment-to-moment awareness of your internal state.</p>
                <ArrowLeft className="w-5 h-5 text-[#2C5530] mt-3" />
              </Link>
              <Link to="/mac/commitment" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B]/50 hover:shadow-md transition-all group" data-testid="nav-commitment">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Next Step</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#BC562B] transition-colors">Step In — Action</h3>
                <p className="text-sm text-[#57534E]">Commit fully to your process and personal values on every shot.</p>
                <ArrowRight className="w-5 h-5 text-[#BC562B] mt-3" />
              </Link>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MACAcceptingPage;
